// authored by Joshua Beedle
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {
  color,
  flexbox,
  layout,
  space,
  typography,
} from 'styled-system';
import IcomoonReact from 'icomoon-react';
import { HiEye, HiEyeOff } from 'react-icons/hi';

import Label from './Label';
import Wrapper from './Wrapper';
import iconSet from '../../images/teambuildr-selection.json';
import CharCounter from './CharCounter';

const ErrorText = styled('div')`
  color: #CC0404;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  margin-top: 5px;
`;

const EyeToggleButton = styled.button`
  position: absolute;
  background: transparent;
  border: none;
  color: #757575;
  font-size: 18px;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
  
  &:hover {
    color: #444444;
  }
`;

const Input = styled('input')`
  border: ${(props) => (props.error ? '1px solid #CC0404' : `1px solid ${props.theme.colors.borderLight}`)};
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.surfaceInput};
  color: ${(props) => props.textColor || props.theme.colors.text};
  display: flex;
  font-family: 'Nunito Sans';
  font-size: ${(props) => (props.fontSize)};
  font-weight: ${(props) => props.fontWeight || 600};
  height: ${(props) => props.height};
  padding: ${(props) => {
    if (props.error) return '0px 25px 0px 12px';
    if (props.type === 'password') return '8px 40px 8px 10px';
    return '8px 10px';
  }};
  text-align: ${(props) => (props.centered && 'center')};
  width: ${(props) => props.width};
  ${color}
  ${flexbox}
  ${layout}
  ${space}
  ${typography}
  
  &::placeholder {
    color: ${(props) => props.placeholderColor || props.theme.colors.textSecondary};
    text-transform: none;
  }
  text-transform: ${(props) => props.textTransformUppercase && 'uppercase'};

  :disabled {
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textTertiary};
  border: 1px solid ${(props) => props.theme.colors.borderLight};
}
`;

const TextArea = styled('textarea')`
  border: ${(props) => (props.error ? '1px solid #CC0404' : `1px solid ${props.theme.colors.borderLight}`)};
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.surfaceInput};
  color: ${(props) => props.textColor || props.theme.colors.text};
  display: flex;
  font-family: 'Nunito Sans';
  font-size: ${(props) => (props.fontSize)};
  font-weight: ${(props) => props.fontWeight || 600};
  height: ${(props) => props.height};
  padding: ${(props) => (props.error ? '0px 25px 0px 12px' : '8px 10px')};
  text-align: ${(props) => (props.centered && 'center')};
  width: ${(props) => props.width};
  ${color}
  ${flexbox}
  ${layout}
  ${space}
  ${typography}
  
  &::placeholder {
    color: ${(props) => props.placeholderColor || props.theme.colors.textSecondary};
  }

  :disabled {
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textTertiary};
  border: 1px solid ${(props) => props.theme.colors.borderLight};
}
`;

/**
 *
 * @param {Bool} autoFocus bool to autofocus the input on mount
 * @param {Bool} centered bool to decide whether or not to center the text
 * @param {Number} charLimit the maximum number of characters allowed in the input
 * @param {Bool} error bool to decide whether or not to display error messages and styling
 * @param {String} errorMsg custom message to display below the input when there is an error
 * @param {String} eyeRight specifies the right-side positioning of eye toggle
 * @param {String} eyeTop specifies the top positioning of eye toggle
 * @param {String} fontSize custom font size for the input
 * @param {Number} fontWeight custom fontWeight for text
 * @param {String} height custom height for the wrapper component
 * @param {Bool} inputOnly bool to decide whether or not to remove the label
 * @param {Bool} isPasswordVisible bool to track if password is visible
 * @param {Bool} isTextArea bool to decide whether or not it will be a textarea
 * @param {Node} label content for the label component (string or JSX)
 * @param {String} name specifies the name of the <input> element
 * @param {Function} onBlur can be used to handle blur events for formik (ie touched)
 * @param {Function} onChange specifies the onChange function that will occur
 * @param {Function} onEyeToggleClick function to handle eye toggle click
 * @param {Function} onKeyUp can be used to handle key up events
 * @param {String} placeholder custom text to display as the placeholder
 * @param {String} placeholderColor custom color for placeholder text
 * @param {String} right specifies the right-side positioning of icon in input on error
 * @param {Bool} showEyeToggle bool to decide whether to show password visibility toggle
 * @param {Object} styledPropsInput custom props for Input
 * @param {Object} styledPropsLabel custom props for Label
 * @param {Object} styledPropsWrapper custom props for Wrapper
 * @param {String} textColor custom color for text
 * @param {String} top specifies the top postioning of icon in input on error
 * @param {String} type handles what the input type is (ex. text, number, password)
 * @param {String} value the value to be shown in the <input> element
 * @param {String} width custom width for the wrapper component
*
*/

const SharedInput = ({
  autoFocus,
  centered,
  charLimit,
  disabled,
  error,
  errorMsg,
  eyeRight,
  eyeTop,
  fontSize,
  fontWeight,
  height,
  inputOnly,
  isPasswordVisible,
  isTextArea,
  label,
  name,
  onBlur,
  onChange,
  onEyeToggleClick,
  onKeyUp,
  placeholder,
  placeholderColor,
  right,
  showEyeToggle,
  styledPropsInput,
  styledPropsLabel,
  styledPropsWrapper,
  textColor,
  top,
  type,
  value,
  width,
}) => (
  <Wrapper {...styledPropsWrapper}>
    {isTextArea ? (
      <>
        {!inputOnly && (
          <Label {...styledPropsLabel} style={{ color: disabled && '#BDBDBD' }}>
            {label}
          </Label>
        )}
        <TextArea
          autoFocus={autoFocus}
          centered={centered}
          disabled={disabled}
          className={error ? 'error' : null}
          error={error}
          fontSize={fontSize}
          fontWeight={fontWeight}
          height={height}
          maxLength={charLimit || null}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
          placeholderColor={placeholderColor}
          textColor={textColor}
          type={type}
          value={value}
          width={width}
          {...styledPropsInput}
        />
        {error && !showEyeToggle ? (
          <IcomoonReact
            color='#CC0404'
            icon='remove'
            iconSet={iconSet}
            size={20}
            style={{
              border: '1px solid #CC0404',
              borderRadius: '50%',
              padding: '1px',
              position: 'absolute',
              right: right || '10px',
              top: top || '8px',
            }}
          />
        ) : null}
        {showEyeToggle ? (
          <EyeToggleButton
            type='button'
            tabIndex='-1'
            onClick={onEyeToggleClick}
            style={{
              right: eyeRight,
              top: eyeTop,
            }}
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? <HiEyeOff /> : <HiEye />}
          </EyeToggleButton>
        ) : null}
        {error ? (
          <ErrorText>{errorMsg}</ErrorText>
        ) : null}
        {charLimit ? (
          <CharCounter isNearLimit={value?.length > (charLimit - 50)}>
            {`${value?.length}/${charLimit}`}
          </CharCounter>
        ) : null}
      </>
    ) : (
      <>
        {!inputOnly && (
          <Label {...styledPropsLabel} style={{ color: disabled && '#BDBDBD' }}>
            {label}
          </Label>
        )}
        <Input
          autoFocus={autoFocus}
          centered={centered}
          disabled={disabled}
          className={error ? 'error' : null}
          error={error}
          fontSize={fontSize}
          fontWeight={fontWeight}
          height={height}
          maxLength={charLimit || null}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          onKeyUp={onKeyUp}
          placeholder={placeholder}
          placeholderColor={placeholderColor}
          textColor={textColor}
          type={type}
          value={value}
          width={width}
          {...styledPropsInput}
        />
        {error && !showEyeToggle ? (
          <IcomoonReact
            color='#CC0404'
            icon='remove'
            iconSet={iconSet}
            size={20}
            style={{
              border: '1px solid #CC0404',
              borderRadius: '50%',
              padding: '1px',
              position: 'absolute',
              right: right || '10px',
              top: top || '8px',
            }}
          />
        ) : null}
        {showEyeToggle ? (
          <EyeToggleButton
            type='button'
            tabIndex='-1'
            onClick={onEyeToggleClick}
            style={{
              right: eyeRight,
              top: eyeTop,
            }}
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? <HiEyeOff /> : <HiEye />}
          </EyeToggleButton>
        ) : null}
        {error ? (
          <ErrorText>{errorMsg}</ErrorText>
        ) : null}
        {charLimit ? (
          <CharCounter isNearLimit={value?.length > (charLimit - 5)}>
            {`${value?.length}/${charLimit}`}
          </CharCounter>
        ) : null}
      </>
    )}
  </Wrapper>
);

SharedInput.propTypes = {
  textTransformUppercase: PropTypes.bool,
  centered: PropTypes.bool,
  charLimit: PropTypes.number,
  error: PropTypes.bool,
  errorMsg: PropTypes.string,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.number,
  height: PropTypes.string,
  inputOnly: PropTypes.bool,
  isTextArea: PropTypes.bool,
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  placeholderColor: PropTypes.string,
  right: PropTypes.string,
  styledPropsInput: PropTypes.instanceOf(Object),
  styledPropsLabel: PropTypes.instanceOf(Object),
  styledPropsWrapper: PropTypes.instanceOf(Object),
  textColor: PropTypes.string,
  top: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  width: PropTypes.string,
  onKeyUp: PropTypes.func,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  showEyeToggle: PropTypes.bool,
  onEyeToggleClick: PropTypes.func,
  isPasswordVisible: PropTypes.bool,
  eyeRight: PropTypes.string,
  eyeTop: PropTypes.string,
};

SharedInput.defaultProps = {
  textTransformUppercase: false,
  centered: false,
  charLimit: null,
  error: false,
  errorMsg: '',
  fontSize: '12px',
  fontWeight: 600,
  height: '',
  inputOnly: false,
  isTextArea: false,
  label: '',
  onBlur: () => {},
  placeholder: '',
  // Empty defaults let the styled fallbacks use theme tokens (light values are
  // identical to the old hardcoded #9E9E9E / #444444).
  placeholderColor: '',
  right: '',
  styledPropsInput: {},
  styledPropsLabel: {},
  styledPropsWrapper: {},
  textColor: '',
  top: '',
  type: 'text',
  width: '',
  onKeyUp: () => {},
  autoFocus: false,
  disabled: false,
  showEyeToggle: false,
  onEyeToggleClick: () => {},
  isPasswordVisible: false,
  eyeRight: '',
  eyeTop: '',
};

export default SharedInput;
